import { ReactNode } from "react";
import {
  ArtifactComponentFragmentFragment,
  BasicGoalArtifactComponentFragmentFragment,
  GoalArtifactSidebarFragmentFragment,
} from "types/graphql-schema";

import ArtifactIcon from "@apps/artifact/components/artifact-icon";
import GoalVisibilityIcon from "@components/goal-visibility-icon/goal-visibility-icon";
import AppLink from "@components/link/link";
import { toObjectProps } from "@components/link/link-helpers";
import { getPrivateGoalTitle } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { isPrivateBasicGoalArtifactNode } from "@helpers/helpers";

import ArtifactInfos from "./artifact-infos";

// The bordered card chrome of an artifact embed. The wysiwyg embed applies
// these classes to its NodeViewWrapper (plus editor-only concerns like the
// selection ring and vertical margin); other surfaces use ArtifactEmbedCard
// below so artifact rows look identical everywhere.
export const artifactEmbedCardClassName =
  "px-1.5 py-1 rounded-md border border-gray-100 bg-artifact-background flex";

export const artifactEmbedTitleClassName =
  "flex flex-1 items-center min-w-0 text-sm font-medium text-gray-800 hover:underline";

// A standalone artifact embed card, visually identical to the wysiwyg
// artifact embed on meeting/document pages. Used outside the editor (e.g.
// the goal page/drawer alignment section) so goals render through the same
// components (ArtifactInfos: due date, progress, participants, dropdown) in
// both places.
const ArtifactEmbedCard = ({
  artifact,
  to,
  dropdownChildren,
  hideDropdown = false,
  className = "",
}: {
  artifact:
    | GoalArtifactSidebarFragmentFragment
    | BasicGoalArtifactComponentFragmentFragment
    | (ArtifactComponentFragmentFragment & {
        __typename: "GoalArtifactNode";
      });
  to?: string | toObjectProps;
  // Extra menu items prepended to the "..." dropdown (e.g. Remove alignment).
  dropdownChildren?: ReactNode;
  hideDropdown?: boolean;
  className?: string;
}) => {
  const isPrivate = isPrivateBasicGoalArtifactNode(artifact);
  const title =
    (artifact.__typename === "BasicGoalArtifactNode"
      ? artifact.goalArtifact?.title || getPrivateGoalTitle()
      : artifact.title) || "";

  return (
    <div className={classNames(artifactEmbedCardClassName, className)}>
      <div className="flex-1 min-w-0">
        <div
          className={classNames(
            "flex flex-1 items-start min-w-0 gap-2",
            "@container/wysiwyg-artifact",
            "not-prose",
          )}
        >
          <div className="flex items-start gap-1">
            <div className="mt-0.5">
              <ArtifactIcon artifact={artifact} size="4" />
            </div>
            <GoalVisibilityIcon
              goalVisibility={artifact.goalVisibility}
              size="4"
              showOnlyIfPrivate
              className="mt-0.5"
            />
          </div>
          {isPrivate || !to ? (
            <span
              className={classNames(
                artifactEmbedTitleClassName,
                "text-gray-400 hover:no-underline",
              )}
            >
              {title}
            </span>
          ) : (
            <AppLink to={to} className={artifactEmbedTitleClassName}>
              {title}
            </AppLink>
          )}
          <ArtifactInfos
            syncedArtifact={artifact}
            loading={false}
            dropdownChildren={dropdownChildren}
            hideDropdown={hideDropdown}
          />
        </div>
      </div>
    </div>
  );
};

export default ArtifactEmbedCard;
